/**
 * @file scheduleStep.component.tsx
 * @path apps/website/src/features/booking/components/steps/scheduleStep.component.tsx
 */

import { RouteStop } from "@sirbastians/types";
import { Button, Card, CardContent, Input, Label } from "@sirbastians/ui";
import { cn } from "@sirbastians/utils";
import { format } from "date-fns";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useBookingStore } from "../booking.store";
import { validateScheduleStep } from "../booking.schema";
import {
  AlarmFill,
  ArrowDown,
  ArrowUp,
  CalendarDateFill,
  GeoAltFill,
  Plus,
  Trash,
} from "react-bootstrap-icons";

// Time slot options (every 30 minutes, 05:00–23:00)
const TIME_SLOTS: string[] = (() => {
  const slots: string[] = [];
  for (let h = 5; h <= 23; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
    if (h < 23) slots.push(`${String(h).padStart(2, "0")}:30`);
  }
  return slots;
})();

export const ScheduleStep = () => {
  const store = useBookingStore();
  const {
    selectedDate,
    selectedTime,
    routeStops,
    selectedService,
    showValidation,
  } = store;

  const validation = showValidation ? validateScheduleStep(store) : null;
  const fieldError = (id: string) =>
    validation?.fieldErrors.find((e) => e.elementId === id)?.message;

  const isRouteEditable = selectedService?.route?.type !== "fixed-itinerary";

  // Fail-safe: initialise stops if missing (e.g. after page refresh)
  useEffect(() => {
    if (selectedService && routeStops.length === 0) {
      const defaultStops: RouteStop[] = (
        selectedService.route?.stops || []
      ).map((stop) => ({
        id: uuidv4(),
        type: (stop.id.includes("pickup")
          ? "pickup"
          : stop.id.includes("dropoff")
            ? "dropoff"
            : "stop") as RouteStop["type"],
        name: stop.name,
        address: stop.address ?? "",
        isEditable: stop.isEditable,
        isExtraStop: false,
      }));

      if (defaultStops.length > 0) {
        store.setRouteStops(defaultStops);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  // Auto-inject activity stops for point-to-point services
  useEffect(() => {
    if (selectedService?.route?.type !== "point-to-point") return;
    const acts = store.selectedActivities;
    if (!acts.length) return;

    const activityStops: RouteStop[] = acts.map((a) => ({
      id: uuidv4(),
      type: "stop",
      name: a.title,
      address: a.location ?? "",
      isEditable: true,
      isExtraStop: true,
    }));

    const pickup: RouteStop = routeStops.find((s) => s.type === "pickup") ?? {
      id: uuidv4(),
      type: "pickup",
      name: "Your Pickup Location",
      address: "",
      isEditable: true,
      isExtraStop: false,
    };

    const dropoff: RouteStop = routeStops.find((s) => s.type === "dropoff") ?? {
      id: uuidv4(),
      type: "dropoff",
      name: "Your Drop-off Location",
      address: "",
      isEditable: true,
      isExtraStop: false,
    };

    store.setRouteStops([pickup, ...activityStops, dropoff]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.selectedActivities, selectedService?.route?.type]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Schedule & Route</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose your date, start time, and confirm your pickup/drop-off points.
        </p>
      </div>

      {/* ── Date ─────────────────────────────────────────────────────────── */}
      <section data-field-id="date-selector">
        <Label className="flex items-center gap-2 mb-2 text-sm font-semibold">
          <CalendarDateFill className="h-4 w-4" /> Date
        </Label>
        <Input
          type="date"
          value={selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""}
          min={format(new Date(), "yyyy-MM-dd")}
          onChange={(e) =>
            store.setDate(e.target.value ? new Date(e.target.value) : undefined)
          }
          className={cn(
            "max-w-xs",
            fieldError("date-selector") && "border-destructive",
          )}
        />
        {fieldError("date-selector") && (
          <p className="mt-1.5 text-xs text-destructive">
            {fieldError("date-selector")}
          </p>
        )}
      </section>

      {/* ── Time ─────────────────────────────────────────────────────────── */}
      <section data-field-id="startTime">
        <Label className="flex items-center gap-2 mb-2 text-sm font-semibold">
          <AlarmFill className="h-4 w-4" /> Start Time
        </Label>
        <div className="flex flex-wrap gap-2 max-h-52 overflow-y-auto pr-2">
          {TIME_SLOTS.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => store.setTime(slot)}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                selectedTime === slot
                  ? "bg-accent text-white border-accent"
                  : "border-border hover:border-accent/50",
              )}
            >
              {slot}
            </button>
          ))}
        </div>
        {fieldError("startTime") && (
          <p className="mt-1.5 text-xs text-destructive">
            {fieldError("startTime")}
          </p>
        )}
      </section>

      {/* ── Route stops ──────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <GeoAltFill className="h-4 w-4" /> Route
          </Label>
          {isRouteEditable && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={store.addStop}
            >
              <Plus className="h-3.5 w-3.5" /> Add Stop
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {routeStops.map((stop, idx) => (
            <StopCard
              key={stop.id}
              stop={stop}
              index={idx}
              total={routeStops.length}
              isEditable={isRouteEditable}
              onAddressChange={(addr) => store.updateStopAddress(stop.id, addr)}
              onMoveUp={() => store.moveStop(idx, "up")}
              onMoveDown={() => store.moveStop(idx, "down")}
              onRemove={() => store.removeStop(stop.id)}
              error={fieldError(`stop-address-${stop.id}`)}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StopCard
// ---------------------------------------------------------------------------

interface StopCardProps {
  stop: RouteStop;
  index: number;
  total: number;
  isEditable: boolean;
  onAddressChange: (addr: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  error?: string;
}

const STOP_LABELS: Record<RouteStop["type"], string> = {
  pickup: "Pickup",
  dropoff: "Drop-off",
  stop: "Stop",
};

const STOP_COLORS: Record<RouteStop["type"], string> = {
  pickup: "bg-green-500",
  dropoff: "bg-red-500",
  stop: "bg-accent",
};

const StopCard = ({
  stop,
  index,
  total,
  isEditable,
  onAddressChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  error,
}: StopCardProps) => (
  <Card
    className={cn(error && "border-destructive")}
    data-field-id={`stop-address-${stop.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        {/* Type indicator */}
        <div className="flex flex-col items-center gap-1 pt-0.5">
          <span
            className={cn(
              "h-3 w-3 rounded-full shrink-0",
              STOP_COLORS[stop.type],
            )}
          />
          {index < total - 1 && (
            <span className="flex-1 w-px bg-border min-h-[20px]" />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {STOP_LABELS[stop.type]}
              {stop.name && stop.name !== STOP_LABELS[stop.type] && (
                <span className="normal-case tracking-normal font-normal ml-1">
                  — {stop.name}
                </span>
              )}
            </span>

            {isEditable && stop.isExtraStop && (
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onMoveUp}
                  disabled={index === 0}
                  aria-label="Move stop up"
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onMoveDown}
                  disabled={index === total - 1}
                  aria-label="Move stop down"
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={onRemove}
                  aria-label="Remove stop"
                >
                  <Trash className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          {stop.isEditable ? (
            <Input
              value={stop.address}
              onChange={(e) => onAddressChange(e.target.value)}
              placeholder={`Enter ${STOP_LABELS[stop.type].toLowerCase()} address…`}
              className={cn(
                "text-sm",
                error && "border-destructive focus-visible:ring-destructive/30",
              )}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {stop.address || "—"}
            </p>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);
